import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, trFold, SearchClear } from "@/components/shared";
import { Send, Copy, Trash2, Link2, Clock, CheckCircle2, Bell, XCircle, FileText, Search } from "lucide-react";

const SCOPE_LABELS = { all: "Tüm teklifler", lowest: "En düşük teklif", top3: "İlk 3", top5: "İlk 5", custom: "Özel seçim" };

export default function ApprovalsPage() {
  const [requests, setRequests] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [savedApprovers, setSavedApprovers] = useState([]);
  const [approvals, setApprovals] = useState(null);
  const [requestId, setRequestId] = useState("");
  const [scope, setScope] = useState("all");
  const [recipientKey, setRecipientKey] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualName, setManualName] = useState("");
  const [approverList, setApproverList] = useState([]);
  const [note, setNote] = useState("");
  const [customOffers, setCustomOffers] = useState([]);
  const [customSel, setCustomSel] = useState([]);
  const [recOfferId, setRecOfferId] = useState("");
  const [sending, setSending] = useState(false);
  const [reqSearch, setReqSearch] = useState("");
  const [remindA, setRemindA] = useState(null);
  const [remindSel, setRemindSel] = useState([]);
  const [remindNote, setRemindNote] = useState("");
  const [remindBusy, setRemindBusy] = useState(false);

  const loadApprovals = () => api.get("/approvals").then((r) => setApprovals(r.data)).catch(() => setApprovals([]));

  useEffect(() => {
    api.get("/requests").then((r) => setRequests(r.data.filter((x) => x.offer_count > 0))).catch(() => {});
    api.get("/approvals/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/settings/approvers").then((r) => setSavedApprovers(r.data.approvers || [])).catch(() => {});
    loadApprovals();
  }, []);

  const selectedReq = requests.find((r) => r.id === requestId);

  useEffect(() => {
    setCustomSel([]);
    setCustomOffers([]);
    setRecOfferId("");
    if (!requestId) return;
    api.get(`/requests/${requestId}`).then((r) => {
      const offers = (r.data.columns || [])
        .filter((c) => c.offer)
        .map((c) => ({ id: c.offer.id, company: c.supplier.company_name, price: c.offer.price, currency: c.offer.currency || "TRY", status: c.offer.status }))
        .sort((a, b) => a.price - b.price);
      setCustomOffers(offers);
    }).catch(() => setCustomOffers([]));
  }, [requestId]);

  useEffect(() => { setRecOfferId(""); }, [scope]);
  useEffect(() => {
    if (recOfferId && !scopedOffers.some((o) => o.id === recOfferId)) setRecOfferId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customSel]);

  const scopedOffers = scope === "lowest" ? customOffers.slice(0, 1)
    : scope === "top3" ? customOffers.slice(0, 3)
    : scope === "top5" ? customOffers.slice(0, 5)
    : scope === "custom" ? customOffers.filter((o) => customSel.includes(o.id))
    : customOffers;

  const isManual = recipientKey === "__manual__";
  const savedMatch = savedApprovers.find((s) => s.email === recipientKey);
  const pickerApprover = isManual
    ? (manualEmail.trim() ? { email: manualEmail.trim(), name: manualName.trim() } : null)
    : savedMatch ? { email: savedMatch.email, name: savedMatch.name }
    : (recipients.find((r) => r.email === recipientKey) ? { email: recipientKey, name: recipients.find((r) => r.email === recipientKey).name } : null);

  const effectiveApprovers = () => {
    const list = [...approverList];
    if (pickerApprover && !list.some((a) => a.email.toLowerCase() === pickerApprover.email.toLowerCase())) list.push(pickerApprover);
    return list.slice(0, 3);
  };

  const addApprover = () => {
    if (!pickerApprover) return toast.error("Önce kişi seçin veya e-posta girin");
    if (approverList.some((a) => a.email.toLowerCase() === pickerApprover.email.toLowerCase())) return toast.error("Bu onaylayıcı zaten listede");
    if (approverList.length >= 3) return toast.error("En fazla 3 onaylayıcı eklenebilir");
    setApproverList((p) => [...p, pickerApprover]);
    setRecipientKey(""); setManualEmail(""); setManualName("");
  };

  const send = async () => {
    if (!requestId) return toast.error("Bir talep seçin");
    if (scope === "custom" && !customSel.length) return toast.error("Özel seçim için en az bir teklif işaretleyin");
    const approvers = effectiveApprovers();
    if (!approvers.length) return toast.error("En az bir onaylayıcı seçin veya e-posta girin");
    setSending(true);
    try {
      const { data } = await api.post("/approvals", { request_id: requestId, scope, offer_ids: scope === "custom" ? customSel : [], recommended_offer_id: recOfferId, note, approvers });
      toast.success(`Onay bağlantısı ${data.approvers.length} onaylayıcıya gönderildi (${data.offer_count} teklif)`);
      setRequestId(""); setScope("all"); setRecipientKey(""); setManualEmail(""); setManualName(""); setNote(""); setApproverList([]); setCustomSel([]); setRecOfferId("");
      loadApprovals();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Bağlantı kopyalandı");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/approvals/${id}`);
      setApprovals((a) => a.filter((x) => x.id !== id));
      toast.success("Onay kaydı silindi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openRemind = (a) => {
    const pend = (a.approvers || []).filter((x) => x.status === "pending").map((x) => x.email);
    setRemindA(a);
    setRemindSel(pend.length ? pend : [a.recipient_email].filter(Boolean));
    setRemindNote("");
  };

  const sendRemind = async () => {
    if (!remindSel.length) return toast.error("En az bir onaylayıcı seçin");
    setRemindBusy(true);
    try {
      const r = await api.post(`/approvals/${remindA.id}/remind`, { emails: remindSel, note: remindNote });
      toast.success(`${r.data.sent.length} onaylayıcıya hatırlatma gönderildi`);
      setRemindA(null);
      loadApprovals();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemindBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="approvals-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Karar Süreci</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Teklif Onayı</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Bir ihale için gelen teklifleri (tümü, en düşük, en düşük 3/5 veya özel seçim) seçtiğiniz kişilere özel bağlantılarla gönderin.
          Birden fazla onaylayıcı eklerseniz teklif ancak <strong>tümü onayladığında</strong> kabul edilir.
        </p>
      </div>

      <div className="border border-slate-200 bg-white p-6 space-y-5 max-w-3xl" data-testid="approval-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>İhale / Talep</Label>
            <Select value={requestId} onValueChange={setRequestId} onOpenChange={(open) => { if (!open) setReqSearch(""); }}>
              <SelectTrigger data-testid="approval-request-select" className="rounded-none h-11 border-slate-200">
                <SelectValue placeholder="Talep seçin" />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                <div className="sticky top-0 z-10 bg-white p-1.5 border-b border-slate-100">
                  <div className="relative">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="approval-request-search"
                      value={reqSearch}
                      onChange={(e) => setReqSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Talep ara..."
                      className="w-full h-8 pl-8 pr-7 text-sm border border-slate-200 outline-none focus:border-slate-400 bg-white"
                    />
                    <SearchClear value={reqSearch} onClear={() => setReqSearch("")} testid="approval-search-clear" />
                  </div>
                </div>
                {requests.filter((r) => !reqSearch || trFold(r.title).includes(trFold(reqSearch))).map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.title} ({r.offer_count} teklif)</SelectItem>
                ))}
                {reqSearch && requests.filter((r) => trFold(r.title).includes(trFold(reqSearch))).length === 0 && (
                  <div className="py-3 text-center text-sm text-slate-400">Sonuç bulunamadı</div>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Gönderilecek Teklifler</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="approval-scope-select" className="rounded-none h-11 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Tüm teklifler{selectedReq ? ` (${selectedReq.offer_count})` : ""}</SelectItem>
                <SelectItem value="lowest">En düşük teklif (yalnızca 1)</SelectItem>
                <SelectItem value="top3">En düşük 3 teklif</SelectItem>
                <SelectItem value="top5">En düşük 5 teklif</SelectItem>
                <SelectItem value="custom">Özel seçim — teklifleri kendim seçeceğim</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {scope === "custom" && requestId && (
          <div className="space-y-1.5" data-testid="custom-offer-picker">
            <Label>Onaya Gönderilecek Teklifler ({customSel.length} seçili)</Label>
            {customOffers.length === 0 ? (
              <p className="text-xs text-slate-400 border border-dashed border-slate-200 px-3 py-3">Teklifler yükleniyor veya bu talepte teklif yok...</p>
            ) : (
              <div className="border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                {customOffers.map((o) => (
                  <label key={o.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150" data-testid={`custom-offer-${o.id}`}>
                    <input
                      type="checkbox"
                      checked={customSel.includes(o.id)}
                      onChange={(e) => setCustomSel((p) => (e.target.checked ? [...p, o.id] : p.filter((x) => x !== o.id)))}
                      className="accent-slate-900"
                      data-testid={`custom-offer-check-${o.id}`}
                    />
                    <span className="text-sm flex-1 min-w-0 truncate font-medium">{o.company}</span>
                    {o.status === "accepted" && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 text-emerald-700">Kabul edildi</span>}
                    {o.status === "rejected" && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 border border-red-200 text-red-600">Reddedildi</span>}
                    <span className="text-sm font-semibold tabular-nums shrink-0">{Number(o.price).toLocaleString("tr-TR")} {o.currency}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {requestId && scopedOffers.length > 0 && (
          <div className="space-y-1.5" data-testid="recommendation-section">
            <Label>Komisyon Tavsiyesi (opsiyonel)</Label>
            <Select value={recOfferId || "__none__"} onValueChange={(v) => setRecOfferId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="approval-recommend-select" className="rounded-none h-11 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72">
                <SelectItem value="__none__">Tavsiye işaretleme</SelectItem>
                {scopedOffers.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    ★ {o.company} — {Number(o.price).toLocaleString("tr-TR")} {o.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400">Seçerseniz onay sayfasında bu teklif "Komisyon Tavsiyesi" rozetiyle işaretlenir; onaylayıcı yine serbestçe seçim yapabilir.</p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Onaylayacak Kişi{approverList.length > 0 ? "ler" : ""}</Label>
          {approverList.length > 0 && (
            <div className="flex flex-wrap gap-2 pb-1" data-testid="approver-chips">
              {approverList.map((a) => (
                <span key={a.email} className="inline-flex items-center gap-1.5 text-xs border border-slate-300 bg-slate-50 px-2.5 py-1.5" data-testid={`approver-chip-${a.email}`}>
                  <strong>{a.name || a.email}</strong> <span className="text-slate-400">({a.email})</span>
                  <button type="button" onClick={() => setApproverList((p) => p.filter((x) => x.email !== a.email))} data-testid={`approver-chip-remove-${a.email}`} className="text-slate-400 hover:text-red-500 transition-colors duration-150">
                    <XCircle size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <div className="flex-1">
              <Select value={recipientKey} onValueChange={setRecipientKey}>
                <SelectTrigger data-testid="approval-recipient-select" className="rounded-none h-11 border-slate-200">
                  <SelectValue placeholder="Kullanıcı seçin veya e-posta girin" />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {savedApprovers.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Kayıtlı Onaylayıcılar</div>
                      {savedApprovers.map((s) => (
                        <SelectItem key={s.id} value={s.email} data-testid={`saved-approver-option-${s.id}`}>
                          ★ {s.name ? `${s.name} — ` : ""}{s.email}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold border-t border-slate-100 mt-1 pt-2">Kullanıcılar</div>
                    </>
                  )}
                  {recipients.filter((u) => !savedApprovers.some((s) => s.email === u.email)).map((u) => (
                    <SelectItem key={u.id} value={u.email}>
                      {u.name} — {u.company} ({u.email}) {u.role === "superadmin" ? "· Yönetici" : "· Müşteri"}
                    </SelectItem>
                  ))}
                  <SelectItem value="__manual__">Diğer — e-posta adresi gireceğim</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {approverList.length < 3 && (
              <Button type="button" variant="outline" onClick={addApprover} data-testid="approval-add-approver" className="rounded-none h-11 border-dashed border-slate-300 text-slate-600 hover:border-slate-500 hover:bg-slate-50 text-xs font-semibold px-4 transition-colors duration-150">
                + Ekle ({effectiveApprovers().length}/3)
              </Button>
            )}
          </div>
          {effectiveApprovers().length > 1 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="multi-approver-note">
              {effectiveApprovers().length} onaylayıcı: teklif ancak TÜM onaylayıcılar onayladığında kabul edilir.
            </p>
          )}
        </div>

        {isManual && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>E-posta</Label>
              <Input value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="kisi@firma.com" data-testid="approval-manual-email" className="rounded-none h-11 border-slate-200" />
            </div>
            <div className="space-y-1.5">
              <Label>Ad Soyad (opsiyonel)</Label>
              <Input value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Ad Soyad" data-testid="approval-manual-name" className="rounded-none h-11 border-slate-200" />
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Not (opsiyonel, e-postaya eklenir)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="örn. Yarın akşama kadar değerlendirmenizi rica ederiz..." data-testid="approval-note-input" className="rounded-none border-slate-200" />
        </div>

        <Button onClick={send} disabled={sending} data-testid="approval-send-button" className="rounded-none h-11 px-8 bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
          <Send size={15} /> {sending ? "Gönderiliyor..." : "Onaya Gönder"}
        </Button>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="approvals-list">
        <div className="px-4 py-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
          Gönderilen Onaylar {approvals ? `(${approvals.length})` : ""}
        </div>
        {!approvals ? (
          <p className="px-4 py-6 text-sm text-slate-500">Yükleniyor...</p>
        ) : approvals.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500" data-testid="no-approvals">Henüz onaya gönderilmiş teklif yok.</p>
        ) : (
          approvals.map((a) => {
            const aps = a.approvers || [];
            const pendingAps = aps.filter((x) => x.status === "pending");
            return (
              <div key={a.id} data-testid={`approval-row-${a.id}`} className="px-4 py-3.5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{a.request_title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {aps.length === 0 && <>{a.recipient_name ? `${a.recipient_name} · ` : ""}{a.recipient_email} · </>}
                    {SCOPE_LABELS[a.scope] || a.scope} ({a.offer_count} teklif{(a.offer_companies || []).length > 0 && a.offer_count <= 5 ? `: ${a.offer_companies.join(", ")}` : ""}) · {formatDate(a.created_at)}
                  </div>
                  {aps.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`approval-approvers-${a.id}`}>
                      {aps.map((x) => (
                        <span key={x.email} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 border ${x.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid={`approval-approver-chip-${a.id}-${x.email}`}>
                          {x.status === "approved" ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                          <strong>{x.name || x.email}</strong>
                          {x.status === "approved"
                            ? `onayladı${x.decided_at ? ` · ${new Date(x.decided_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}`
                            : "bekleniyor"}
                          {x.status === "pending" && x.reminder_count > 0 && <span className="text-[10px] text-amber-500">({x.reminder_count} hatırlatma)</span>}
                          {x.link && (
                            <button type="button" onClick={() => copyLink(x.link)} title="Bu onaylayıcının bağlantısını kopyala" data-testid={`approval-approver-link-${a.id}-${x.email}`} className="text-slate-400 hover:text-slate-700 transition-colors duration-150">
                              <Copy size={10} />
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                  {a.approved_offer && (
                    <div className="text-xs text-emerald-700 mt-0.5 font-medium" data-testid={`approval-result-${a.id}`}>
                      Onaylanan: {a.approved_offer.company_name} — {a.approved_offer.price.toLocaleString("tr-TR")} {a.approved_offer.currency}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.status === "approved" ? (
                    <Badge variant="outline" className="rounded-none text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><CheckCircle2 size={11} /> Onaylandı</Badge>
                  ) : aps.length > 1 && aps.some((x) => x.status === "approved") ? (
                    <Badge variant="outline" className="rounded-none text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock size={11} /> {aps.filter((x) => x.status === "approved").length}/{aps.length} Onay</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-none text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><Clock size={11} /> Bekliyor</Badge>
                  )}
                  {a.status === "pending" && (pendingAps.length > 0 || aps.length === 0) && (
                    <Button size="sm" variant="outline" onClick={() => openRemind(a)} data-testid={`approval-remind-${a.id}`} className="rounded-none h-8 border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 gap-1.5 text-xs font-semibold">
                      <Bell size={12} /> Hatırlat
                    </Button>
                  )}
                  {a.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const res = await api.get(`/approvals/${a.id}/pdf`, { responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const el = document.createElement("a");
                        el.href = url;
                        el.download = `onay_tutanagi_${a.id.slice(0, 8)}.pdf`;
                        el.click();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Tutanak PDF oluşturulamadı"); }
                    }} data-testid={`approval-pdf-${a.id}`} className="rounded-none h-8 border-slate-300 hover:bg-slate-100 gap-1.5 text-xs">
                      <FileText size={12} /> Tutanak
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => copyLink(a.link)} data-testid={`approval-copy-${a.id}`} className="rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs">
                    <Copy size={12} /> Bağlantı
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(a.link, "_blank")} data-testid={`approval-open-${a.id}`} className="rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs">
                    <Link2 size={12} /> Aç
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(a.id)} data-testid={`approval-delete-${a.id}`} className="rounded-none h-8 border-red-500/30 text-red-500 hover:bg-red-500/10">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={!!remindA} onOpenChange={(o) => { if (!o) setRemindA(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="approval-remind-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <Bell size={18} className="text-amber-600" /> Onay Hatırlatması Gönder
            </DialogTitle>
          </DialogHeader>
          {remindA && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600"><strong>{remindA.request_title}</strong> için kararı beklenen onaylayıcılar:</p>
              <div className="space-y-2">
                {((remindA.approvers || []).filter((x) => x.status === "pending").length
                  ? remindA.approvers.filter((x) => x.status === "pending").map((x) => ({ name: x.name, email: x.email, rc: x.reminder_count || 0 }))
                  : [{ name: remindA.recipient_name, email: remindA.recipient_email, rc: remindA.reminder_count || 0 }]
                ).map((x) => (
                  <label key={x.email} className="flex items-center gap-3 border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50 transition-colors duration-150" data-testid={`approval-remind-approver-${x.email}`}>
                    <input
                      type="checkbox"
                      checked={remindSel.includes(x.email)}
                      onChange={(e) => setRemindSel((p) => (e.target.checked ? [...p, x.email] : p.filter((y) => y !== x.email)))}
                      className="accent-slate-900"
                    />
                    <span className="text-sm">
                      <strong>{x.name || x.email}</strong> <span className="text-slate-400 text-xs">({x.email})</span>
                      {x.rc > 0 && <span className="text-[11px] text-amber-600 ml-2">{x.rc} kez hatırlatıldı</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Ek Not (opsiyonel)</Label>
                <Textarea value={remindNote} onChange={(e) => setRemindNote(e.target.value)} rows={2} data-testid="approval-remind-note" placeholder="Hatırlatma e-postasına eklenecek not..." className="rounded-none bg-white border-slate-200" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRemindA(null)} data-testid="approval-remind-cancel" className="flex-1 rounded-none h-11 border-slate-200 hover:bg-slate-100 transition-colors duration-200">Vazgeç</Button>
                <Button onClick={sendRemind} disabled={remindBusy} data-testid="approval-remind-send" className="flex-1 rounded-none h-11 bg-amber-600 hover:bg-amber-500 text-white gap-2 font-semibold transition-colors duration-200">
                  <Bell size={14} /> {remindBusy ? "Gönderiliyor..." : "Hatırlatma Gönder"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
